import { z } from "zod";
/**
 * "Dados errados?" — a person telling us the catalogue is wrong about one
 * exercise (`D199`). The wire shape behind `POST /catalog/exercises/:id/reports`.
 *
 * ── WHY A REASON AND NOT ONLY A NOTE ────────────────────────────────────────
 * The report is read by the owner in the daily digest (`D180`), counted per
 * exercise, and what makes a count actionable is knowing WHICH part of the
 * record is wrong: a wrong clip is a media fix, a wrong muscle is a data fix,
 * and the two go to different places. A free-text field alone would make
 * every report a sentence to read before it could be sorted. The note is the
 * optional detail on top.
 *
 * The five values are the CHECK on `exercise_report.reason` (`0028`), word for
 * word — a sixth here would be a 500 at the INSERT rather than a 422 at the
 * route.
 */
export const exerciseReportReason = z.enum([
    "video",
    "muscles",
    "instructions",
    "name",
    "other",
]);
/** The same five, as a list the sheet can render its choices from. */
export const EXERCISE_REPORT_REASONS = exerciseReportReason.options;
/**
 * The note's ceiling, in characters, AFTER trimming. A report is a pointer at a
 * record, not an essay — "the clip shows a dumbbell, the name says barbell" is
 * sixty characters — and the digest prints none of it, so a long note buys
 * nothing and is one more place a person's words are stored.
 */
export const EXERCISE_REPORT_NOTE_MAX = 500;
/**
 * `POST /catalog/exercises/:id/reports`.
 *
 * ── `id` IS REQUIRED, AND IT IS WHAT MAKES A RETRY SAFE (`D163`) ────────────
 * A client-minted UUIDv7, the same id every other replayable write here
 * carries. A repeat of the same `id` by the same account answers the report
 * already stored — 200 instead of 201 — and writes nothing, so a resend after
 * a timeout is not a second report inflating the digest's count. There is no
 * un-keyed form: the endpoint is new, so no installed app sends one without.
 *
 * Mint it with the `uuidv7` helper, never `crypto.randomUUID()` — that does not
 * exist on the live-reload origin (`T-174`).
 *
 * Strict, like every input (`D172`): an unknown key is 422
 * `request.invalid_body`, not a field silently dropped.
 */
export const exerciseReportInput = z.strictObject({
    id: z.uuid(),
    /** Optional detail. Blank after trimming is the same as absent. */
    note: z.string().trim().max(EXERCISE_REPORT_NOTE_MAX).optional(),
    reason: exerciseReportReason,
});
/**
 * The stored report's answer. On a replay it is the FIRST attempt's
 * `reportedAt`, which is the truth the client must see, not the moment of the
 * retry.
 */
export const exerciseReportOutput = z.object({
    id: z.uuid(),
    reportedAt: z.iso.datetime(),
});
/**
 * The real path for one exercise's reports — the id encoded, for the reason
 * `exerciseDetailPath` gives: the server's pattern carries Hono's `:id`, and a
 * client that string-replaced it would be a second encoder to keep in
 * agreement with the server's decoder.
 */
export function exerciseReportsPath(exerciseId) {
    return `/catalog/exercises/${encodeURIComponent(exerciseId)}/reports`;
}
//# sourceMappingURL=exercise-report.js.map